import React, { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { CompanyFormData } from '../types';
import { useCreateCompany } from '@/core/api/queries/companies';
import { useUIState } from '@/core/UIStateProvider';

interface CreateCompanyModalProps {
  showCreateCompany: boolean;
  setShowCreateCompany: (show: boolean) => void;
}

export const CreateCompanyModal: React.FC<CreateCompanyModalProps> = ({ showCreateCompany, setShowCreateCompany }) => {
  const [newCompanyData, setNewCompanyData] = useState<CompanyFormData>({
    name: '',
    email: '',
    phone: '',
    role: 'LSP',
  });
  const { addNotification } = useUIState();
  const createCompany = useCreateCompany();
  const handleCreateCompany = async (): Promise<void> => {
    try {
      await createCompany.mutateAsync(newCompanyData);
      addNotification('success', 'Company created successfully!');
      setShowCreateCompany(false);
      setNewCompanyData({ name: '', email: '', phone: '', role: 'LSP' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create company';
      addNotification('error', errorMessage);
    }
  };

  if (!showCreateCompany) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">Create New Company</h3>
          <button
            onClick={() => setShowCreateCompany(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
            <input
              type="text"
              value={newCompanyData.name}
              onChange={(e) => setNewCompanyData({ ...newCompanyData, name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter company name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
            <input
              type="email"
              value={newCompanyData.email}
              onChange={(e) => setNewCompanyData({ ...newCompanyData, email: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="company@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
            <input
              type="tel"
              value={newCompanyData.phone}
              onChange={(e) => setNewCompanyData({ ...newCompanyData, phone: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Optional phone number"
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => setShowCreateCompany(false)}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateCompany}
            disabled={!newCompanyData.name || !newCompanyData.email || createCompany.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors flex items-center space-x-2"
          >
            {createCompany.isPending && <Loader2 size={16} className="animate-spin" />}
            <span>Create Company</span>
          </button>
        </div>
      </div>
    </div>
  );
};
